class LoyaltyPointsQuickview {
  constructor() {
    if (LoyaltyPointsQuickview.instance) {
      return LoyaltyPointsQuickview.instance;
    }
    
    LoyaltyPointsQuickview.instance = this;
    
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized) {
      return;
    }
    
    this.setupGlobalListeners();
    
    document.addEventListener('theme:quick-add:open', this.handleQuickviewOpen.bind(this));
    
    document.addEventListener('theme:variant:change', this.handleVariantChange.bind(this));
    
    document.addEventListener('change', this.handleGenericChange.bind(this));
    document.addEventListener('input', this.handleGenericInput.bind(this));
    
    this.setupContentObserver();
    
    this.initialized = true;
  }

  setupGlobalListeners() {
    // Listen for subscription changes using event delegation
    document.addEventListener('change', (event) => {
      const quickview = event.target.closest('[data-quick-add-modal]');
      if (!quickview) return;

      // Check if it's a subscription-related change
      const isSubscriptionChange = 
        (event.target.closest('.subscription-card') && event.target.name === 'selling-plan-group') ||
        (event.target.closest('.subscription-plan-option') && event.target.name === 'selling_plan');

      if (isSubscriptionChange) {
        setTimeout(() => this.renderLoyaltyPoints(quickview), 100);
      }
    }, true);
  }

  setupContentObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.querySelector && node.querySelector('[data-product-upsell-ajax]')) {
                setTimeout(() => {
                  this.initializeLoyaltyPoints(node);
                }, 100);
              }
              
              if (node.hasAttribute && node.hasAttribute('data-quick-add-modal')) {
                setTimeout(() => {
                  this.initializeLoyaltyPoints(node);
                }, 100);
              }
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  handleQuickviewOpen(event) {
    setTimeout(() => {
      const quickview = document.querySelector('[data-quick-add-modal][open]');
      if (quickview) {
        this.initializeLoyaltyPoints(quickview);
      }
    }, 200);
  }

  handleVariantChange(event) {
    const quickview = event.target.closest('[data-quick-add-modal]');
    if (quickview) {
      setTimeout(() => this.renderLoyaltyPoints(quickview), 50);
    }
  }

  handleGenericChange(event) {
    const quickview = event.target.closest('[data-quick-add-modal]');
    if (quickview && event.target.name?.startsWith('options')) {
      setTimeout(() => this.renderLoyaltyPoints(quickview), 100);
    }
  }

  handleGenericInput(event) {
    const quickview = event.target.closest('[data-quick-add-modal]');
    if (quickview && event.target.name?.startsWith('options')) {
      setTimeout(() => this.renderLoyaltyPoints(quickview), 100);
    }
  }

  // Helper: Parse money string to cents
  parseMoneyToCents(text) {
    if (!text) return null;
    let numeric = text.replace(/[^\d.,]/g, '');
    if (numeric.includes(',') && !numeric.includes('.')) {
      numeric = numeric.replace(',', '.');
    } else if (numeric.includes(',') && numeric.includes('.')) {
      const lastComma = numeric.lastIndexOf(',');
      const lastDot = numeric.lastIndexOf('.');
      numeric = lastComma > lastDot 
        ? numeric.replace(/\./g, '').replace(',', '.')
        : numeric.replace(/,/g, '');
    }
    const price = parseFloat(numeric);
    return isNaN(price) ? null : Math.round(price * 100);
  }

  // Extract price from subscription widget
  getSubscriptionPrice(container) {
    const subscriptionWidget = container.querySelector('quick-subscription');
    if (!subscriptionWidget) return null;

    const onetimeInput = subscriptionWidget.querySelector('input[data-onetime-toggle]');
    const subscribeInput = subscriptionWidget.querySelector('input[data-subscribe-toggle]');
    
    if (onetimeInput?.checked) {
      const onetimePriceEl = subscriptionWidget.querySelector('[data-onetime-price]');
      if (onetimePriceEl) {
        return this.parseMoneyToCents(onetimePriceEl.textContent.trim());
      }
    } else if (subscribeInput?.checked) {
      const currentPriceEl = subscriptionWidget.querySelector('.subscription-card__current');
      if (currentPriceEl) {
        return this.parseMoneyToCents(currentPriceEl.textContent.trim());
      }
    }
    
    return null;
  }

  initializeLoyaltyPoints(container) {
    if (!container.querySelector('[data-loyalty-points-quickview]')) {
      this.renderLoyaltyPoints(container);
    }
  }

  renderLoyaltyPoints(container) {
    // Get price from subscription widget first, then fallback to variant price
    let price = this.getSubscriptionPrice(container);
    
    if (!price) {
      const productForm = container.querySelector('product-form');
      if (productForm?.productForm) {
        const formState = productForm.productForm.getFormState();
        price = formState.variant?.price || null;
      }
    }

    if (!price) return;

    const points = Math.floor((price / window.Shopify.currency.rate) / 100);
    const loyaltyContainer = container.querySelector('[data-loyalty-points-quickview]');
    
    if (!loyaltyContainer) {
      const newContainer = this.createLoyaltyPointsContainer(points);
      const form = container.querySelector('form');
      if (form) {
        const firstFormElement = form.querySelector('.form-field, [data-product-form], product-form');
        if (firstFormElement) {
          firstFormElement.parentNode.insertBefore(newContainer, firstFormElement);
        } else {
          form.insertBefore(newContainer, form.firstChild);
        }
      }
    } else {
      this.updateLoyaltyPointsDisplay(container, points);
    }
  }

  updateLoyaltyPointsDisplay(container, points) {
    const pointsElement = container.querySelector('.loyalty-points-value');
    if (pointsElement) {
      pointsElement.textContent = points;
    }
  }

  createLoyaltyPointsContainer(points) {
    const container = document.createElement('div');
    container.className = 'mb-4 loyalty-points-info';
    container.setAttribute('data-loyalty-points-quickview', '');
    
    const isCustomer = window.Shopify && window.Shopify.customer && window.Shopify.customer.id;
    
    let loginText = '';
    if (!isCustomer) {
      loginText = '<a href="/account/login" class="font-bold underline">Login/Sign Up</a>';
    }
    
    container.innerHTML = `
      <div class="loyalty-points-info__text">
        Earn <span class="loyalty-points-value">${points}</span> points for this product.
        ${loginText}
      </div>
    `;
    
    return container;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.loyaltyPointsQuickview = new LoyaltyPointsQuickview();
});

if (!window.loyaltyPointsQuickview) {
  window.loyaltyPointsQuickview = new LoyaltyPointsQuickview();
}

